
import Vue from "vue";
import { Component, Prop, Watch } from "vue-property-decorator";
import { getModule } from "vuex-module-decorators";
import { OrderModule, AccountModule, FacilityModule, SystemModule } from "@/store/modules/index";
import notifications from "../../../lib/notifications";
import SearchFilterEditor from "../SearchFilterEditor.vue";
import * as modals from "@/lib/modals";
import { IAdHocFilter } from "@/store/types";

const orderModule = getModule(OrderModule);
const accountModule = getModule(AccountModule);
const facilityModule = getModule(FacilityModule);
const systemModule = getModule(SystemModule);

@Component({
  name: modals.manageOrderFiltersModal,
  components: {
    SearchFilterEditor
  }
})
export default class ManageOrderFiltersModal extends Vue {
  localValue: boolean = false;
  selectedFilter: Object = null;
  isSaving: boolean = false;
  detailView: boolean = false;
  adhocFilter: Object = { searchFields: [] };
  facilityFilter: any = null;
  showFacility: boolean = true;
  isSearchOrderInactiveInclude: boolean = false;
  isSavedSearchInactiveInclude: boolean = false;

  @Prop({ default: false })
  value: boolean;

  get filters() {
    return accountModule.orderSearches;
  }

  get facilityFilters() {
    return facilityModule.facilityOrderSearches;
  }

  get facilities() {
    return systemModule.facilities;
  }

  get showDetail() {
    return this.detailView;
  }

  get filteredFacilityFilters() {
    if (this.facilityFilter)
      return this.facilityFilters.filter(x => x.facilityCode == this.facilityFilter);
    return this.facilityFilters;
  }

  get defaultSavedSearch() {
    return (
      accountModule.defaultSavedSearchesUserSettings &&
      parseInt(accountModule.defaultSavedSearchesUserSettings[0])
    );
  }

  get defaultSearchSubscription() {
    return (
      accountModule.defaultSearchSubscriptionsUserSettings &&
      parseInt(accountModule.defaultSearchSubscriptionsUserSettings[0])
    );
  }

  @Watch("value")
  valueChanged(val) {
    this.localValue = val;
  }

  async resetFilter() {
    accountModule.resetAdhocFilter();
    this.hide();
    await orderModule.reloadOrders();
  }

  async mounted() {
    this.loadFacilities();
  }

  async loadFacilities() {
    await systemModule.loadFacilitiesWithInclude();
  }

  load() {
    this.isSearchOrderInactiveInclude =
      facilityModule.context.rootState.facility.includeSearchOrderInactiveFacilities;
    this.isSavedSearchInactiveInclude =
      facilityModule.context.rootState.facility.includeSavedSearchInactiveFacilities;
    let activeFilter = accountModule.getAdhocFilterIfActive;
    if (!activeFilter) activeFilter = accountModule.getSelectedSavedOrderSearch;
    if (activeFilter) {
      this.adhocFilter = JSON.parse(JSON.stringify(activeFilter));
    } else {
      this.adhocFilter = { searchFields: [] };
    }
    if (this.facilities && this.facilities.length == 1) {
      this.showFacility = false;
    }
  }

  formatDateRanges(filter: IAdHocFilter) {
    filter.searchFields.forEach(field => {
      if (
        field.dateType === "fixed" &&
        field.filterValues[0] &&
        typeof field.filterValues[0] === "string"
      ) {
        const dateData = field.filterValues[0].split(" ~ ");
        if (dateData) {
          const startDate = dateData[0] && dateData[0].indexOf("null") < 0 ? dateData[0] : null;
          const endDate = dateData[1] && dateData[1].indexOf("null") < 0 ? dateData[1] : startDate;
          field.filterValues[0] = `${startDate} ~ ${endDate}`;
        }
      }
    });
  }

  async search(filter: IAdHocFilter) {
    this.formatDateRanges(filter);
    accountModule.setAdhocFilter(filter);
    accountModule.setLastOrderSearch(filter);
    this.hide();
    await orderModule.reloadOrders();
  }

  async save(filter) {
    this.formatDateRanges(filter);
    let payload = {
      id: filter.id,
      searchName: filter.searchName,
      searchFields: filter.searchFields.map(sf => {
        if (sf.name == "RefNumbers") {
          return {
            name: "RefNumbers",
            dataType: sf.dataType,
            filterValues: sf.filterValues.map(fv => JSON.stringify(fv))
          };
        }
        if (sf.name == "DeliveryForIds") {
          return {
            name: "DeliveryForIds",
            dataType: sf.dataType,
            filterValues: sf.filterValues.map(fv => fv.toString())
          };
        }
        return sf;
      })
    };

    this.isSaving = true;

    try {
      await accountModule.saveOrderSearch(payload);
    } catch (ex) {
      this.isSaving = false;
      notifications.error("Save Search", "Failed to save search", ex);
      return;
    }

    try {
      await accountModule.getSavedOrderSearches();
      this.showList();
    } catch (ex) {
      notifications.error("Save Search", "Failed to load existing searches.", ex);
    }

    this.isSaving = false;
  }

  async saveDefaultSavedSearches(filter) {
    try {
      await accountModule.updateUserSettings({
        settingName: "defaultSavedSearches",
        values: [filter.id.toString()]
      });
      await this.removeDefaultSearchSubscriptions();
      await accountModule.setLastOrderSearch({
        text: filter.searchName,
        value: filter.id
      });
    } catch (ex) {
      notifications.error("Save Default Search", "Failed to save default search", ex);
    }
  }

  async removeDefaultSavedSearches() {
    try {
      await accountModule.updateUserSettings({
        settingName: "defaultSavedSearches"
      });
      await accountModule.setLastOrderSearch(null);
    } catch (ex) {
      notifications.error("Remove Default Search", "Failed to remove default search", ex);
    }
  }

  async saveDefaultSearchSubscriptions(filter) {
    try {
      await accountModule.updateUserSettings({
        settingName: "defaultSearchSubscriptions",
        values: [filter.id.toString()]
      });
      await this.removeDefaultSavedSearches();
      await accountModule.setLastOrderSearch({
        value: -(filter.id + 1),
        text: filter.searchName
      });
    } catch (ex) {
      notifications.error(
        "Save Default Search Subscription",
        "Failed to save default search subscription",
        ex
      );
    }
  }

  async removeDefaultSearchSubscriptions() {
    try {
      await accountModule.updateUserSettings({
        settingName: "defaultSearchSubscriptions"
      });
      await accountModule.setLastSearchSubscription(null);
    } catch (ex) {
      notifications.error(
        "Remove Default search Subscription",
        "Failed to remove default search subscription",
        ex
      );
    }
  }

  addFilter() {
    this.selectedFilter = { searchName: null, searchFields: [] };
    this.detailView = true;
  }

  showList() {
    this.selectedFilter = { searchFields: [] };
    this.detailView = false;
  }

  edit(filter) {
    this.selectedFilter = JSON.parse(JSON.stringify(filter));
    this.detailView = true;
  }

  hide() {
    this.selectedFilter = { searchFields: [] };
    this.detailView = false;
    this.$emit("input", false);
  }

  deleteFilter(filter) {
    this.$bvModal
      .msgBoxConfirm(`Are you sure you want to remove the filter '${filter.searchName}'?`)
      .then(value => {
        if (value) {
          accountModule.deleteOrderSearch(filter.id);
          if (filter.id === this.defaultSavedSearch) {
            this.removeDefaultSavedSearches();
          }
          if (
            !accountModule.defaultSavedSearchesUserSettings?.length &&
            !accountModule.defaultSearchSubscriptionsUserSettings?.length &&
            !accountModule.lastOrderSearch &&
            !accountModule.lastSearchSubscription
          ) {
            accountModule.setSelectedSavedOrderSearch(null);
          }
        }
      })
      .catch(err => {
        console.warn(err);
      });
  }

  async toggleSubscribeToFacilityFilter(id, event) {
    try {
      if (event) {
        await accountModule.subscribeToFaciltiySearch(id);
      } else {
        await accountModule.unsubscribeToFaciltiySearch(id);
        if (id === this.defaultSearchSubscription) {
          await this.removeDefaultSearchSubscriptions();
        }
      }
    } catch (ex) {
      notifications.error("Subscribe", "Failed to subscribe to saved search.", ex);
    }

    try {
      await facilityModule.getFacilitySavedOrderSearches();
    } catch (ex) {
      notifications.error("Save Search", "Failed to load existing searches.", ex);
    }
  }
}
